package terraultima.web

import kotlinx.browser.document
import kotlinx.browser.window

/**
 * The Telegram Mini App SDK, behind feature detection.
 *
 * Everything here is `dynamic` on purpose. `window.Telegram.WebApp` is a moving target — one object
 * whose members appeared across a dozen Bot API versions, served to whatever client the player
 * happens to have installed — and a typed `external interface` turns a missing method on an old
 * Android build into a hard crash on a page with no way to report it. So every call is guarded, and
 * every guard has a browser fallback, because THE SAME PAGE MUST WORK OUTSIDE TELEGRAM: that is how
 * a cold-read gets a plain link (`docs/03-PLAYTEST-METHOD.md` §8) instead of a "please open this in
 * Telegram" wall.
 */
object Tg {

    private val app: dynamic = try {
        window.asDynamic().Telegram?.WebApp
    } catch (e: Throwable) { null }

    /** Running inside a Telegram client, as opposed to a plain browser tab. */
    val inTelegram: Boolean get() = app != null && js("typeof app.initData === 'string'") as Boolean

    val platform: String get() = (app?.platform as? String) ?: "browser"

    private fun has(name: String): Boolean =
        app != null && js("typeof app[name] === 'function'") as Boolean

    private fun sub(name: String): dynamic = if (app == null) null else app[name]

    // ------------------------------------------------------------------ lifecycle

    fun boot() {
        applyTheme()
        // The stylesheet needs to know: outside Telegram the page grows its own bottom button, and
        // the tab bar has to move up out of its way.
        document.body?.classList?.add(if (inTelegram) "in-tg" else "no-tg")
        if (app == null) return
        runCatching { if (has("ready")) app.ready() }
        runCatching { if (has("expand")) app.expand() }
        // Bot API 7.7. Without it, a downward swipe inside a scrolling decision closes the app —
        // which on a long event body happens constantly and looks like a crash.
        runCatching { if (has("disableVerticalSwipes")) app.disableVerticalSwipes() }
        runCatching { if (has("onEvent")) app.onEvent("themeChanged", { applyTheme() }) }
        runCatching {
            if (has("onEvent")) app.onEvent("viewportChanged", { applyViewport() })
        }
        applyViewport()
    }

    /**
     * Telegram's theme, as CSS custom properties.
     *
     * Newer clients set `--tg-theme-*` themselves; older ones only expose `themeParams`. Copying
     * them over unconditionally makes one stylesheet work on every client and, with the fallbacks in
     * `styles.css`, in a browser that has no Telegram at all.
     */
    fun applyTheme() {
        val root = document.documentElement ?: return
        val params = sub("themeParams")
        if (params != null) {
            val keys = js("Object.keys(params)") as Array<String>
            for (k in keys) {
                val v = params[k] as? String ?: continue
                root.asDynamic().style.setProperty("--tg-theme-" + k.replace('_', '-'), v)
            }
        }
        val scheme = (app?.colorScheme as? String) ?: if (
            window.matchMedia("(prefers-color-scheme: dark)").matches) "dark" else "light"
        root.setAttribute("data-scheme", scheme)
        // The header and the page must agree, or a dark app sits under a white bar.
        runCatching { if (has("setHeaderColor")) app.setHeaderColor("secondary_bg_color") }
    }

    private fun applyViewport() {
        val h = (app?.viewportStableHeight as? Double) ?: return
        document.documentElement?.asDynamic()?.style?.setProperty("--tg-viewport", "${h}px")
    }

    // ------------------------------------------------------------------ feedback

    /** A decision landing should be felt. Silent everywhere it is unsupported. */
    /**
     * Hands the run back to the bot. Telegram delivers this only when the app was opened from the
     * bot's keyboard button, and then closes the app; opened any other way it does nothing, which
     * the caller detects by the app still being open a moment later.
     */
    fun sendData(data: String): Boolean {
        if (!has("sendData")) return false
        return try { app.sendData(data); true } catch (e: Throwable) { false }
    }

    fun close() { if (has("close")) try { app.close() } catch (e: Throwable) {} }

    fun haptic(kind: String) {
        val h = sub("HapticFeedback") ?: return
        runCatching {
            when (kind) {
                "warning", "error", "success" -> h.notificationOccurred(kind)
                else -> h.impactOccurred(kind)   // light | medium | heavy | rigid | soft
            }
        }
    }

    /** Telegram's own modal, so a locked option reads as part of the client, not the page. */
    fun alert(message: String) {
        if (app != null && has("showAlert")) runCatching { app.showAlert(message); return }
        window.alert(message)
    }

    fun confirm(message: String, then: (Boolean) -> Unit) {
        if (app != null && has("showConfirm")) {
            runCatching { app.showConfirm(message, { ok: Boolean -> then(ok) }); return }
        }
        then(window.confirm(message))
    }

    // ------------------------------------------------------------------ the main button

    private var mainHandler: (() -> Unit)? = null

    /**
     * The bottom button is where a Telegram app puts its one irreversible action, so it is where
     * this game commits a decision. A choice you have not committed can be changed; that is the
     * whole reason the option cards select rather than act.
     */
    fun mainButton(text: String?, destructive: Boolean = false, onClick: (() -> Unit)? = null) {
        val mb = sub("MainButton")
        // The SDK is loaded in a plain browser too, where its MainButton exists and draws nothing.
        if (mb == null || !inTelegram) { fallbackMainButton(text, onClick, destructive); return }
        runCatching {
            if (mainHandler != null) mb.offClick(mainHandler)
            mainHandler = null
            if (text == null) { mb.hide(); return }
            mb.setText(text)
            if (destructive) runCatching {
                mb.setParams(js("({color: '#d1453b', text_color: '#ffffff'})"))
            }
            val h = { onClick?.invoke(); Unit }
            mainHandler = h
            mb.onClick(h)
            mb.show()
            mb.enable()
        }.onFailure { fallbackMainButton(text, onClick, destructive) }
    }

    /** A browser has no bottom button, so the page grows one. */
    private fun fallbackMainButton(text: String?, onClick: (() -> Unit)?, destructive: Boolean) {
        val existing = document.getElementById("fallback-main")
        if (text == null) { existing?.remove(); return }
        val btn = existing ?: document.createElement("button").also {
            it.id = "fallback-main"
            it.className = "fallback-main"
            document.body?.appendChild(it)
        }
        btn.textContent = text
        btn.className = "fallback-main" + if (destructive) " destructive" else ""
        btn.asDynamic().onclick = { onClick?.invoke(); Unit }
    }

    // ------------------------------------------------------------------ the back button

    private var backHandler: (() -> Unit)? = null

    fun backButton(onClick: (() -> Unit)?) {
        val bb = sub("BackButton")
        if (bb == null) return
        runCatching {
            if (backHandler != null) bb.offClick(backHandler)
            backHandler = null
            if (onClick == null) { bb.hide(); return }
            val h = { onClick(); Unit }
            backHandler = h
            bb.onClick(h)
            bb.show()
        }
    }

    // ------------------------------------------------------------------ persistence

    /**
     * `CloudStorage` keeps up to 1,024 keys per user ON TELEGRAM'S SERVERS, which is what makes a
     * century survive a new phone with no backend of ours anywhere. A run is `{seed, motive,
     * choices}` — a few hundred bytes, well under the 4,096-byte value cap.
     *
     * Outside Telegram, `localStorage`. Same shape, one device.
     */
    fun cloudGet(key: String, then: (String?) -> Unit) {
        val cs = sub("CloudStorage")
        if (cs == null || !(js("typeof cs.getItem === 'function'") as Boolean)) {
            then(runCatching { window.localStorage.getItem(key) }.getOrNull()); return
        }
        runCatching {
            cs.getItem(key, { err: dynamic, value: dynamic ->
                if (err != null) then(runCatching { window.localStorage.getItem(key) }.getOrNull())
                else then(value as? String)
                Unit
            })
        }.onFailure { then(runCatching { window.localStorage.getItem(key) }.getOrNull()) }
    }

    fun cloudSet(key: String, value: String) {
        // Always write locally too: it costs nothing and it is the only copy if the cloud call
        // fails or the client is too old to have one.
        runCatching { window.localStorage.setItem(key, value) }
        val cs = sub("CloudStorage") ?: return
        if (!(js("typeof cs.setItem === 'function'") as Boolean)) return
        runCatching { cs.setItem(key, value, { _: dynamic, _: dynamic -> Unit }) }
    }

    fun cloudRemove(key: String) {
        runCatching { window.localStorage.removeItem(key) }
        val cs = sub("CloudStorage") ?: return
        if (!(js("typeof cs.removeItem === 'function'") as Boolean)) return
        runCatching { cs.removeItem(key, { _: dynamic, _: dynamic -> Unit }) }
    }
}
